import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import YotpoStars from 'src/components/YotpoStars'
import { graphql, useStaticQuery } from 'gatsby'
import { useTranslation } from 'gatsby-plugin-react-i18next'

import { GatsbyImage } from 'gatsby-plugin-image'
import {
  Container,
  TextBox,
  Text,
  CenteredOnMobile,
  Section,
  VideoContainer,
  HeroVideo,
  SubHeader,
  BuyNowLink,
  Item,
  ListItem,
  CheckMark,
  BuyNowAnchor,
  ExternalLink,
  Subheading,
  Disclosure,
  BogoContainer,
} from './styles'
import { BUY_LINK, IN_AU, IN_CA, IN_UK, IS_FOREIGN_COUNTRY } from '../../utils/constants'
import heroVideo from '../../videos/frame-video.mp4'
import amazonHeroVideo from '../../videos/frame-mothers-day.mp4'
import amazonPrimeVideo from '../../videos/frame-prime-day.mp4'

import Analytics from '../../utils/Analytics'
import { Intro } from '../CalMaxHero/styles'

const Hero = ({ page, title, subheading, ctaText }) => {
  const { t } = useTranslation('common')

  const { check, link, bogo } = useStaticQuery(graphql`
    query HeroQuery {
      check: file(relativePath: { eq: "checkmark.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, placeholder: NONE)
        }
      }
      link: file(relativePath: { eq: "external-link.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, placeholder: NONE)
        }
      }
      bogo: file(relativePath: { eq: "frame-bogo-badge.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, placeholder: NONE)
        }
      }
    }
  `)

  const getAmazonLink = () => {
    if (IN_UK) return 'https://urlgeni.us/amazon/shop-amz-uk'
    if (IN_CA) return 'https://urlgeni.us/amazon/shop-amz-ca'
    if (IN_AU)
      return 'https://www.amazon.com.au/Skylight-Frame-Digital-Picture-Anywhere/dp/B01N7ENHO6'

    return 'https://urlgeni.us/amazon/shoptraffic'
  }

  const AMAZON_LINK = getAmazonLink()

  const [videoControls, setVideoControls] = useState(false)

  useEffect(() => {
    if (
      window.navigator.userAgent.indexOf('Mozilla') !== -1 &&
      window.navigator.userAgent.indexOf('Android') !== -1
    ) {
      setVideoControls(true)
    }
  }, [])

  const itemList = [
    t('No app or subscription required'),
    t('Quick 1-minute setup — easy for all ages'),
    t('Effortless touchscreen display'),
  ]

  const itemListMD = [
    t('Preload photos without opening the box'),
    t('Quick 1-minute setup - easy for all ages'),
    t('Effortless touchscreen display'),
    t('The gift that keeps giving!'),
  ]
  const items = page === 'amazonMD' || page === 'amazonPrime' ? itemListMD : itemList

  const isFramePage = {
    eyebrow: 'The gift that keeps loved ones connected.',
    heading: 'Skylight Digital Picture Frame',
    description:
      'Skylight is a digital picture frame you can send photos to, and they appear in seconds!',
  }
  const isFrameAmazonMDPage = {
    eyebrow: `Top-Rated Mother's Day Gift`,
    heading: 'Skylight Digital Picture Frame',
    description:
      'Keep Mom connected to any family moment with the digital picture frame that shares photos in seconds',
  }

  const isFrameAmazonPrimePage = {
    eyebrow: `Top-Rated Amazon Prime Day Gift`,
    heading: 'Skylight Digital Picture Frame',
    description:
      'A great surprise gift for the Grandparents. Keep them in the loop and the family close with this easy to use digital picture frame. They will love it!',
  }

  const { eyebrow, description } =
    page === 'amazonMD'
      ? isFrameAmazonMDPage
      : page === 'amazonPrime'
      ? isFrameAmazonPrimePage
      : isFramePage

  const isamazonPage = page === 'amazon' || page === 'amazonMD' || page === 'amazonPrime'
  const videoSrc =
    page === 'amazonMD' ? amazonHeroVideo : page === 'amazonPrime' ? amazonPrimeVideo : heroVideo

  return (
    <Section>
      <Container>
        <TextBox>
          <YotpoStars textcolor="#444444" linkUrl={BUY_LINK} />
          <Intro>{t(eyebrow)}</Intro>
          <Text>{title || isFramePage.heading}</Text>
          <Subheading>{t(description)}</Subheading>
          {items.map((item, i) => (
            <Item key={i}>
              <CheckMark image={check.childImageSharp.gatsbyImageData} alt="check mark" />
              <ListItem>{item}</ListItem>
            </Item>
          ))}
          {subheading && <SubHeader>{subheading}</SubHeader>}
          {isamazonPage ? (
            <>
              <CenteredOnMobile>
                <BuyNowAnchor
                  href={AMAZON_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => Analytics.track('Clicked to Amazon Frame')}
                >
                  {page === 'amazonMD' || page === 'amazonPrime' ? (
                    <>
                      Now <s>$159.99</s> {page === 'amazonPrime' ? '$118.99' : '$99.99'} on Amazon
                    </>
                  ) : (
                    <>{ctaText || t('Buy Now')}</>
                  )}
                  <ExternalLink image={link.childImageSharp.gatsbyImageData} alt="external link" />
                </BuyNowAnchor>
              </CenteredOnMobile>
              {page === 'amazon' && (
                <CenteredOnMobile>
                  <BuyNowLink
                    to={BUY_LINK}
                    onClick={() => Analytics.track('Clicked Buy Now on Frame Amazon Landing Page')}
                  >
                    Buy Now
                  </BuyNowLink>
                </CenteredOnMobile>
              )}
            </>
          ) : (
            <CenteredOnMobile>
              <BuyNowLink to={BUY_LINK} amz={page === 'amazonMD' || page === 'amazonPrime'}>
                {ctaText || t('Buy Now')}
              </BuyNowLink>
            </CenteredOnMobile>
          )}
          {page === 'amazonMD' && (
            <Disclosure>Lowest price ever. Sale ends on 5/11 or while supplies last</Disclosure>
          )}
        </TextBox>
        <VideoContainer>
          <HeroVideo playsInline autoPlay muted loop controls={videoControls}>
            <source src={videoSrc} type="video/mp4" />
          </HeroVideo>
          {!IS_FOREIGN_COUNTRY && (
            <BogoContainer>
              <GatsbyImage
                image={bogo.childImageSharp.gatsbyImageData}
                alt="Buy One Get One 40% off any Frame"
              />
            </BogoContainer>
          )}
        </VideoContainer>
      </Container>
    </Section>
  )
}

export default Hero

Hero.propTypes = {
  page: PropTypes.string,
  title: PropTypes.string,
  subheading: PropTypes.string,
  ctaText: PropTypes.string,
}
